import { Temporal } from '@js-temporal/polyfill';
import { Tooltip, TooltipTrigger } from '@backstage/ui';
import { Focusable } from 'react-aria-components';
import type { Placement } from 'react-aria-components';
import { formatDistanceToNowStrict } from 'date-fns';

type TimestampProps = {
  timestamp: string | Date | Temporal.ZonedDateTime | number;
  displayTimezone: 'UTC' | 'local';
  labelVariant?: 'exact' | 'simple' | 'verbose' | 'relativeDay';
  tooltipPlacement?: Placement;
};

const getUserLocale = () => {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return 'en-GB';
};

export const convertToTemporalInstant = (
  timestamp: string | Date | Temporal.ZonedDateTime | Temporal.Instant | number,
): Temporal.Instant | undefined => {
  if (timestamp instanceof Temporal.ZonedDateTime) {
    return timestamp.toInstant();
  }
  if (timestamp instanceof Temporal.Instant) {
    return timestamp;
  }
  if (timestamp instanceof Date) {
    return Temporal.Instant.fromEpochMilliseconds(timestamp.getTime());
  }
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) {
    return undefined;
  }
  return Temporal.Instant.fromEpochMilliseconds(date.getTime());
};

export function getTemporalLabelText(temporalInstant: Temporal.Instant | null) {
  if (!temporalInstant) {
    return 'Invalid date';
  }

  const now = Temporal.Now.instant();
  const deltaSeconds = now.epochSeconds - temporalInstant.epochSeconds;
  if (deltaSeconds >= 0 && deltaSeconds < 30) {
    return 'A few seconds ago';
  } else if (deltaSeconds > -30 && deltaSeconds <= 0) {
    return 'In a few seconds';
  }

  return formatDistanceToNowStrict(temporalInstant.epochMilliseconds, {
    addSuffix: true,
  });
}

export const Timestamp = (props: TimestampProps) => {
  const {
    timestamp,
    labelVariant = 'simple',
    tooltipPlacement = 'bottom',
    displayTimezone,
  } = props;

  const temporalInstant = convertToTemporalInstant(timestamp);
  if (temporalInstant === undefined) {
    return <span>Invalid date</span>;
  }

  if (labelVariant === 'exact') {
    return (
      <span>
        {temporalInstant.toLocaleString(getUserLocale(), {
          timeZoneName: 'short',
          timeZone: displayTimezone === 'UTC' ? 'UTC' : undefined,
        })}
      </span>
    );
  }

  if (labelVariant === 'relativeDay') {
    return (
      <span>
        {formatRelativeDay(
          temporalInstant,
          getUserLocale(),
          displayTimezone === 'UTC' ? 'UTC' : Temporal.Now.timeZoneId(),
        )}
      </span>
    );
  }

  if (labelVariant === 'verbose') {
    return (
      <span>
        {temporalInstant.toLocaleString(getUserLocale(), {
          dateStyle: 'full',
          timeStyle: 'long',
          timeZone: displayTimezone === 'UTC' ? 'UTC' : undefined,
        })}
      </span>
    );
  }
  return (
    <TooltipTrigger>
      <Focusable>
        <span role="button">{getTemporalLabelText(temporalInstant)}</span>
      </Focusable>
      <Tooltip placement={tooltipPlacement}>
        {temporalInstant.toLocaleString(getUserLocale(), {
          timeZoneName: 'short',
          timeZone: displayTimezone === 'UTC' ? 'UTC' : undefined,
        })}
      </Tooltip>
    </TooltipTrigger>
  );
};

/**
 * "yesterday at 9:10 AM", "today at 2:35 PM" or "tomorrow at 9:10 AM". A time further away shows its
 * date, because "in 3 days at 2:35 PM" reads badly.
 */
function formatRelativeDay(
  instant: Temporal.Instant,
  locale: string,
  timeZone: string,
): string {
  const dayDiff = instant
    .toZonedDateTimeISO(timeZone)
    .toPlainDate()
    .since(Temporal.Now.instant().toZonedDateTimeISO(timeZone).toPlainDate(), {
      largestUnit: 'day',
    }).days;

  if (Math.abs(dayDiff) > 1) {
    return instant.toLocaleString(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone,
    });
  }

  const day = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
    dayDiff,
    'day',
  );
  const time = instant.toLocaleString(locale, { timeStyle: 'short', timeZone });

  return `${day}${getDateTimeConnector(instant, locale, timeZone)}${time}`;
}

/** The locale's own word between a date and a time, such as " at " in English or " um " in German. */
function getDateTimeConnector(
  instant: Temporal.Instant,
  locale: string,
  timeZone: string,
): string {
  const parts = new Intl.DateTimeFormat(locale, {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone,
  }).formatToParts(instant.epochMilliseconds);
  const firstTimePart = parts.findIndex(
    ({ type }) => type === 'hour' || type === 'dayPeriod',
  );
  const before = parts[firstTimePart - 1];

  return before?.type === 'literal' ? before.value : ' ';
}
